import moment from 'moment-timezone';

import { ActorSide, ActorSource } from './actor';
import { ORDER_TYPE } from './tn-order';

export const PORT_OUT_PIN_MAX_LENGTH = 8;
export const PORT_OUT_PIN_MIN_LENGTH = 1;

export const PORTING_TIME_ZONE = 'US/Central';

/** True when the two timestamps fall on the same US/Central (PORTING_TIME_ZONE) calendar day. */
export function isSamePortingDay(a: Date | string, b: Date | string): boolean {
  return moment.tz(a, PORTING_TIME_ZONE).format('YYYY-MM-DD') === moment.tz(b, PORTING_TIME_ZONE).format('YYYY-MM-DD');
}
export const PORTING_MIN_BUSINESS_DAYS = 3;
export const PORTING_START_HOUR = 7;
export const PORTING_END_HOUR = 17;
export const PORTING_TIME_INCREMENT_MINUTES = [0, 15, 30, 45] as const;
export const PORT_ORDER_REJECTION_REASON_MAX_LENGTH = 255;
export const PORT_ORDER_REMARKS_MAX_LENGTH = 255;

export const INTERNAL_SPIDS = ['478J', '551G', '732J'];

export const OFFNET_SUPPLIERS = {
  'BANDWIDTH': 'BANDWIDTH',
  'INTELIQUENT': 'INTELIQUENT',
} as const;

export type OffnetSupplier = keyof typeof OFFNET_SUPPLIERS;

export const OFFNET_PORTING_DISABLED_MESSAGE = 'Off-net porting is disabled for this account.';

// Display names for the Manage Providers page and create-time errors (POR-205).
export const OFFNET_SUPPLIER_LABELS: Record<OffnetSupplier, string> = {
  BANDWIDTH: 'Bandwidth',
  INTELIQUENT: 'Inteliquent',
};

export function isOffnetSupplier(supplier: string | null | undefined): supplier is OffnetSupplier {
  return !!supplier && Object.prototype.hasOwnProperty.call(OFFNET_SUPPLIERS, supplier);
}

export const SUPPLIER_DESTINATION_SPIDS: Record<string, string> = {
  // onnet suppliers
  'PSTN Control': '478J',
  'On Net': '551G',
  'COTN': '478J',
  'COTN 732J': '732J',
  'SNCH 732J': '732J',

  // offnet suppliers
  'INTELIQUENT': '505B',
  'BANDWIDTH': '9996',

  // toll-free supplier
  'TOLLFREE': 'IDBW1',
}

// On-net supplier preference order, highest first.
// Used to pick a single lrn_footprint row when a TN's (lata, rateCenter, state)
// matches multiple on-net supplier rows. Applied in portabilityCheck.
// 'On Net' is intentionally excluded — it is only valid on the alreadyOnNet path.
export const ONNET_SUPPLIER_PREFERENCE = [
  'COTN',
  'COTN 732J',
  'SNCH 732J',
  'PSTN Control',
] as const;

export function pickPreferredOnnetFootprint<T extends { supplier: string }>(
  candidates: T[],
): T | undefined {
  if (!candidates || candidates.length === 0) return undefined;
  for (const supplier of ONNET_SUPPLIER_PREFERENCE) {
    const hit = candidates.find(c => c.supplier === supplier);
    if (hit) return hit;
  }
  return undefined;
}

// Off-net supplier preference order, highest first.
// Used in Phase 2 of portabilityCheck to route each unportable TN to off-net
// carriers based on whether it is MAYBE_PORTABLE (in our footprint but no
// agreement) or NOT_PORTABLE (outside our footprint or unmatched losing SPID).
//
// MAYBE_PORTABLE TNs go to Bandwidth first because Bandwidth's footprint
// overlaps ours more often for these geographies; NOT_PORTABLE TNs go to
// Inteliquent first because that's the broader off-net default.
export const OFFNET_SUPPLIER_PREFERENCE_MAYBE_PORTABLE = [
  'BANDWIDTH',
  'INTELIQUENT',
] as const;

export const OFFNET_SUPPLIER_PREFERENCE_NOT_PORTABLE = [
  'INTELIQUENT',
  'BANDWIDTH',
] as const;

// `disabled` (POR-205) drops admin-disabled providers from the list, so a TN is never
// asked of one and the remaining providers move up a tier.
export function getOffnetSupplierPreference(
  maybePortable: boolean,
  disabled?: ReadonlySet<string>,
): readonly string[] {
  const preference: readonly string[] = maybePortable
    ? OFFNET_SUPPLIER_PREFERENCE_MAYBE_PORTABLE
    : OFFNET_SUPPLIER_PREFERENCE_NOT_PORTABLE;
  return disabled?.size ? preference.filter(s => !disabled.has(s)) : preference;
}

export const PORTABLE_STATUSES = {
  PORTABLE: 'PORTABLE',
  NOT_PORTABLE: 'NOT_PORTABLE',
  MAYBE_PORTABLE: 'MAYBE_PORTABLE',
} as const;

export type PortableStatus = (typeof PORTABLE_STATUSES)[keyof typeof PORTABLE_STATUSES];

/** Display labels, shared by the CSV export and the portability check page. */
export const PORTABLE_STATUS_LABELS: Record<PortableStatus, string> = {
  PORTABLE: 'Portable',
  MAYBE_PORTABLE: 'Maybe Portable',
  NOT_PORTABLE: 'Not Portable',
};

export function getPortableStatus(portable: boolean, maybePortable: boolean): PortableStatus {
  if (portable) {
    return PORTABLE_STATUSES.PORTABLE;
  } else if (maybePortable) {
    return PORTABLE_STATUSES.MAYBE_PORTABLE;
  } else {
    return PORTABLE_STATUSES.NOT_PORTABLE;
  }
}

export function getDestinationSpid(supplier: string) {
  return SUPPLIER_DESTINATION_SPIDS[supplier];
}

export const PORT_TYPES = {
  'INTERNAL': 'INTERNAL',
  'EXTERNAL_TO_INTERNAL': 'EXTERNAL_TO_INTERNAL',
  'EXTERNAL_TO_EXTERNAL': 'EXTERNAL_TO_EXTERNAL',
  'MANUAL': 'MANUAL',
  'TOLLFREE': 'TOLLFREE',
}

export const PORT_SOURCE_TYPES = {
  'INTERNAL': 'INTERNAL',
  'TURNING_POINT': 'TURNING_POINT',
  'MANUAL': 'MANUAL',
  'SOMOS': 'SOMOS',
  // POR-33: legacy ATL-originated port orders migrated from sidewinder. The live
  // create path never produces this source (all external creates → TURNING_POINT);
  // the migration sets it from the structural classifier (atl_port_orders link) so
  // POR-90 can find these post-migration by `source` without a sidewinder join.
  'ATL': 'ATL',
}

export const PORT_SUBMISSION_MODES = {
  AUTO: 'AUTO',
  MANUAL: 'MANUAL',
} as const;

export const PORT_STATUS = {
  SUBMISSION_PENDING: 'SUBMISSION_PENDING',
  SUBMISSION_FAILED: 'SUBMISSION_FAILED',

  UPDATE_POSTED: 'UPDATE_POSTED',

  DATE_CHANGE_POSTED: 'DATE_CHANGE_POSTED',
  DATE_CHANGE_PENDING: 'DATE_CHANGE_PENDING',

  FOC_PENDING: 'FOC_PENDING',
  FOC_RECEIVED: 'FOC_RECEIVED',

  ACTIVATION_PENDING: 'ACTIVATION_PENDING',
  ACTIVATION_IN_PROGRESS: 'ACTIVATION_IN_PROGRESS',
  ACTIVATION_CONFLICT: 'ACTIVATION_CONFLICT',
  ACTIVATION_FAILED: 'ACTIVATION_FAILED',

  LSR_APPROVED: 'LSR_APPROVED',

  REJECTED: 'REJECTED',
  COMPLETED: 'COMPLETED',
  PARTIAL_COMPLETE: 'PARTIAL_COMPLETE',

  CANCELLATION_PENDING: 'CANCELLATION_PENDING',
  CANCELLATION_FAILED: 'CANCELLATION_FAILED',
  CANCELLED: 'CANCELLED',

  FAILED: 'FAILED',

  // legacy statuses — no longer assigned by any event, but existing
  // tn_port_order rows may still carry them
  UPDATE_RECEIVED: 'UPDATE_RECEIVED',
  UPDATE_PENDING_FOC: 'UPDATE_PENDING_FOC',

  // generic fallback statuses for unmapped events or noop events
  UNKNOWN: 'UNKNOWN',
  NA: 'NA',

  // birth status: order created, nothing submitted to any vendor yet
  // (includes maybe-portable orders awaiting a TP agreement)
  NEW: 'NEW',
  // birth status for TOLLFREE orders awaiting ROC submission to Somos.
  // Placeholder name — will be renamed once the final name is decided.
  TOLLFREE_NEW: 'TOLLFREE_NEW',
  // POR-202: the kept TNs of a Turning Point order that had TNs removed after it got a
  // PON. Created only by Remove TNs, never set by an admin; submitted to Turning Point
  // once the order it replaces (replaces_port_order_id) is confirmed Cancelled.
  HOLD: 'HOLD',
} as const;

export const PORT_EVENT = {
  // UI events
  SUBMIT_NEW_PORT_ORDER: 'Submit new port order',
  UPDATE_ORDER_DETAILS: 'Update order details',
  CHANGE_PORT_DATE: 'Change port date',
  CANCEL_PORT_ORDER: 'Cancel port order',
  RESUBMIT_AFTER_REJECTION: 'Resubmit after rejection',
  ACTIVATE_PORT: 'Activate port',
  CHANGE_PORT_TIME: 'Change port time',

  // turning point events
  // NOTE: string values are the O2CI wire statuses (O2CI API spec v4a),
  // which can differ from the display names in the porting event model
  // spreadsheet (e.g. wire 'Completed' is displayed as 'Complete').
  SERVICE_REQUEST_RECEIVED: 'Service Request Received',
  CSR_REQUESTED: 'CSR Requested',
  CSR_IN_PROGRESS: 'CSR In-Progress',
  CSR_RECEIVED: 'CSR Received',
  CSR_FAILED: 'CSR Failed',
  LSR_IN_PROGRESS: 'LSR In-Progress',
  LSR_SUPPLEMENT_IN_PROGRESS: 'LSR Supplement In-Progress',
  SUBMITTED: 'Submitted',
  PENDING_FROM_CARRIER: 'Pending From Carrier',
  FIRM_ORDER_CONFIRMATION: 'Firm Order Confirmation',
  SUPPLEMENT_RECEIVED: 'Supplement Received',
  SUBMIT_FAILED: 'Submit Failed',
  VALIDATION_FAILED: 'Validation Failed',
  SERVICE_REQUEST_INITIATION_FAILED: 'Service Request Initiation Failed',
  REJECTED: 'Rejected',
  JEOPARDY: 'Jeopardy',
  PROVIDER_NOTIFICATION: 'Provider Notification',
  PROVIDER_INITIATED_CANCELLATION: 'Provider Initiated Cancellation',
  CANCEL_CONFIRMATION: 'Cancel Confirmation',
  CANCELLED: 'Cancelled',
  SERVICE_ORDER_CONFIRMATION: 'Service Order Confirmation',
  BILLING_COMPLETION_NOTICE: 'Billing Completion Notice',
  COMPLETED: 'Completed',
  PARTIAL_COMPLETED: 'Partial completed',
  PORT_SCHEDULED: 'Port Scheduled',
  PORT_RESCHEDULED: 'Port Rescheduled',
  PORT_RESCHEDULED_FAILED: 'Port Rescheduled Failed',
  PORT_SCHEDULED_FAILED: 'Port Scheduled Failed',
  SV_ORDER_RECEIVED: 'SV Order Received',
  SV_CREATE_INITIATED: 'SV Create Initiated',
  SV_CREATE_SUBMITTED: 'SV Create Submitted',
  SV_CREATE_REJECTED: 'SV Create Rejected',
  SV_CREATE_FAILED: 'SV Create Failed',
  SV_PENDING: 'SV Pending',
  SV_NEW: 'SV New',
  SV_MODIFY_INITIATED: 'SV Modify Initiated',
  SV_MODIFY_SUBMITTED: 'SV Modify Submitted',
  SV_MODIFY_REJECTED: 'SV Modify Rejected',
  SV_MODIFY_FAILED: 'SV Modify Failed',
  SV_CONCURRED: 'SV Concurred',
  SV_CONCURRENCE_SENT: 'SV Concurrence Sent',
  SV_ACTIVATE_READY: 'SV Activate Ready',
  SV_PARTIAL_FAILED: 'SV Partial Failed',
  SV_CONFLICT: 'SV Conflict',
  SV_ERROR: 'SV Error',
  SV_VALIDATION_FAILED: 'SV Validation Failed',
  SV_OLD: 'SV Old',
  SV_CONFLICT_RECEIVED: 'SV Conflict Received',
  SV_REL_FAILURE: 'SV REL Failure',
  SV_REMOVE_FROM_CONFLICT_INITIATED: 'SV Remove From Conflict Initiated',
  SV_REMOVE_FROM_CONFLICT_SUBMITTED: 'SV Remove From Conflict Submitted',
  SV_REMOVE_FROM_CONFLICT_REJECTED: 'SV Remove From Conflict Rejected',
  SV_REMOVE_FROM_CONFLICT_FAILED: 'SV Remove From Conflict Failed',
  SV_REMOVE_CONFLICT_RECEIVED: 'SV Remove Conflict Received',
  SV_ACTIVATE_RECEIVED: 'SV Activate Received',
  SV_ACTIVATE_INITIATED: 'SV Activate Initiated',
  SV_ACTIVATE_SUBMITTED: 'SV Activate Submitted',
  SV_ACTIVATE_REJECTED: 'SV Activate Rejected',
  SV_ACTIVATE_FAILED: 'SV Activate Failed',
  SV_ACTIVATED: 'SV Activated',
  SOA_COMPLETED: 'SOA Completed',
  SV_CANCEL_INITIATED: 'SV Cancel Initiated',
  SV_CANCEL_SUBMITTED: 'SV Cancel Submitted',
  SV_CANCEL_PENDING: 'SV Cancel Pending',
  SV_CANCEL_REJECTED: 'SV Cancel Rejected',
  SV_CANCEL_FAILED: 'SV Cancel Failed',
  SV_CANCELLED: 'SV Cancelled',
  SOA_CANCELLED: 'SOA Cancelled',
  SV_MODIFY_CANCEL_UNDO_INITIATED: 'SV Modify Cancel Undo Initiated',
  SV_MODIFY_CANCEL_UNDO_SUBMITTED: 'SV Modify Cancel Undo Submitted',
  SV_MODIFY_CANCEL_UNDO_REJECTED: 'SV Modify Cancel Undo Rejected',
  SV_MODIFY_CANCEL_UNDO_FAILED: 'SV Modify Cancel Undo Failed',
  SV_DISCONNECT_INITIATED: 'SV Disconnect Initiated',
  SV_DISCONNECT_SUBMITTED: 'SV Disconnect Submitted',
  SV_DISCONNECT_REJECTED: 'SV Disconnect Rejected',
  SV_DISCONNECT_FAILED: 'SV Disconnect Failed',
  SV_DISC_PENDING: 'SV DISC Pending',
  SV_DISC_SUCCESS: 'SV DISC Success',
  SV_FAILED: 'SV Failed',
  SV_MODIFY_REQUEST_RECEIVED: 'SV Modify Request Received',
  SV_CANCEL_REQUEST_RECEIVED: 'SV Cancel Request Received',

  // somos and bandwidth events
  RESP_ORG_CHANGE_SUBMITTED: 'Resp Org Change Submitted',
  RESP_ORG_UPDATED: 'Resp Org Updated',
  NUMBER_HAS_BEEN_LOADED_TO_CIC: 'Number has been loaded to CIC',
  TEMPLATE_UPDATED: 'Template Updated',

  INTERNAL_PORT_CREATED: 'Internal Port Created',
  // Decision on the Salesforce Port Out case raised for an internal port
  INTERNAL_PORT_OUT_APPROVED: 'Internal Port Out Approved',
  INTERNAL_PORT_OUT_REJECTED: 'Internal Port Out Rejected',
  INTERNAL_PORT_ACTIVATED: 'Internal Port Activated',
} as const;

type PortEvent = typeof PORT_EVENT[keyof typeof PORT_EVENT];
export type PortStatus = typeof PORT_STATUS[keyof typeof PORT_STATUS];
export type PortType = typeof PORT_TYPES[keyof typeof PORT_TYPES];

export const TN_EXTERNAL_PORT_ORDER_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.NEW,
  PORT_STATUS.SUBMISSION_PENDING,
  PORT_STATUS.SUBMISSION_FAILED,
  PORT_STATUS.UPDATE_POSTED,
  PORT_STATUS.DATE_CHANGE_POSTED,
  PORT_STATUS.DATE_CHANGE_PENDING,
  PORT_STATUS.FOC_PENDING,
  PORT_STATUS.FOC_RECEIVED,
  PORT_STATUS.ACTIVATION_PENDING,
  PORT_STATUS.ACTIVATION_IN_PROGRESS,
  PORT_STATUS.ACTIVATION_CONFLICT,
  PORT_STATUS.ACTIVATION_FAILED,
  PORT_STATUS.LSR_APPROVED,
  PORT_STATUS.REJECTED,
  PORT_STATUS.COMPLETED,
  PORT_STATUS.PARTIAL_COMPLETE,
  PORT_STATUS.CANCELLATION_PENDING,
  PORT_STATUS.CANCELLATION_FAILED,
  PORT_STATUS.CANCELLED,
  PORT_STATUS.FAILED,
];

export const TN_INTERNAL_PORT_ORDER_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.FOC_PENDING,
  PORT_STATUS.FOC_RECEIVED,
  PORT_STATUS.REJECTED,
  PORT_STATUS.COMPLETED,
  PORT_STATUS.CANCELLED,
];

export const TN_TOLLFREE_PORT_ORDER_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.TOLLFREE_NEW,
  PORT_STATUS.SUBMISSION_PENDING,
  PORT_STATUS.FOC_PENDING,
  PORT_STATUS.LSR_APPROVED,
  PORT_STATUS.FOC_RECEIVED,
  PORT_STATUS.ACTIVATION_IN_PROGRESS,
  PORT_STATUS.REJECTED,
  PORT_STATUS.COMPLETED,
  PORT_STATUS.CANCELLED,
];

export function getTnSetPortOrderStatusAllowedStatuses(
  portType: string | null | undefined,
): readonly PortStatus[] {
  if (portType === PORT_TYPES.INTERNAL) {
    return TN_INTERNAL_PORT_ORDER_STATUSES;
  }

  if (portType === PORT_TYPES.TOLLFREE) {
    return TN_TOLLFREE_PORT_ORDER_STATUSES;
  }

  if (
    portType === PORT_TYPES.EXTERNAL_TO_INTERNAL ||
    portType === PORT_TYPES.EXTERNAL_TO_EXTERNAL ||
    portType === PORT_TYPES.MANUAL
  ) {
    return TN_EXTERNAL_PORT_ORDER_STATUSES;
  }

  return [];
}

// Statuses in which a general port-order field update (tnUpdatePortOrder) is
// permitted. In these (pre-FOC / exception) states the due date is just another
// editable field and travels inside the type-3 update supplement.
export const TN_UPDATE_PORT_ORDER_ALLOWED_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.NEW,
  PORT_STATUS.TOLLFREE_NEW,
  PORT_STATUS.SUBMISSION_PENDING,
  PORT_STATUS.SUBMISSION_FAILED,
  PORT_STATUS.FOC_PENDING,
  PORT_STATUS.REJECTED,
  PORT_STATUS.UPDATE_POSTED,
  // POR-202: a HOLD order has no PON yet, so an edit persists locally only.
  PORT_STATUS.HOLD,
];

// Statuses in which only the due date may change (tnChangeDueDate), via the
// type-2 supplement. These are post-FOC / date-change states.
export const TN_CHANGE_DUE_DATE_ALLOWED_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.LSR_APPROVED,
  PORT_STATUS.FOC_RECEIVED,
  PORT_STATUS.DATE_CHANGE_POSTED,
  PORT_STATUS.DATE_CHANGE_PENDING,
  PORT_STATUS.ACTIVATION_CONFLICT,
  PORT_STATUS.ACTIVATION_PENDING,
  PORT_STATUS.ACTIVATION_FAILED
];

// Statuses in which a non-tollfree port order may be cancelled (tnCancelPortOrder).
export const TN_CANCEL_PORT_ORDER_ALLOWED_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.NEW,
  PORT_STATUS.SUBMISSION_PENDING,
  PORT_STATUS.SUBMISSION_FAILED,
  PORT_STATUS.FOC_PENDING,
  PORT_STATUS.REJECTED,
  PORT_STATUS.UPDATE_POSTED,
  PORT_STATUS.LSR_APPROVED,
  PORT_STATUS.FOC_RECEIVED,
  PORT_STATUS.DATE_CHANGE_POSTED,
  PORT_STATUS.DATE_CHANGE_PENDING,
  PORT_STATUS.ACTIVATION_CONFLICT,
  PORT_STATUS.ACTIVATION_PENDING,
  PORT_STATUS.ACTIVATION_FAILED,
  PORT_STATUS.CANCELLATION_FAILED,
  // POR-202: a HOLD order has no PON yet, so it cancels locally.
  PORT_STATUS.HOLD,
  PORT_STATUS.FAILED,
];

// Tollfree orders cancel from a narrower set of states than non-tollfree ones.
export const TN_CANCEL_TOLLFREE_PORT_ORDER_ALLOWED_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.TOLLFREE_NEW,
  PORT_STATUS.SUBMISSION_PENDING,
  PORT_STATUS.SUBMISSION_FAILED,
  PORT_STATUS.FOC_PENDING,
  PORT_STATUS.REJECTED,
  PORT_STATUS.UPDATE_POSTED,
  PORT_STATUS.CANCELLATION_FAILED,
  PORT_STATUS.FAILED,
];

// ---------------------------------------------------------------------------
// Manual / scheduled activation gate (POR-173): tnActivationRefusal().
//
// Two conditions, both required, shared by the Activate button, Activate All,
// tnActivatePortOrder (cpaas-api + the portal-api hook) and the scheduled cron
// (whose SQL pre-filter mirrors it with isAdmin = true), so no path can disagree
// on what "activatable" means:
//
// 1. Status, per order type. Turning Point external orders wait for TP's
//    `SV Activate Ready` (PORT_EVENT.SV_ACTIVATE_READY), which moves them to
//    ACTIVATION_PENDING; FOC_RECEIVED alone is too early. Other types never get
//    that event, so they keep the pre-POR-173 list.
// 2. Day window, in US/Eastern calendar days, anchored on focDate ?? dueDate
//    (focDate is written only for TP orders; dueDate is required at creation).
//    The anchor day is open to anyone; the following TN_ACTIVATION_ADMIN_WINDOW_DAYS
//    to admins and the cron.
//
// Sharing the list with the cron is only safe because every path that fires an
// activation retires its activation row (see activationStatusAfterFiring); a
// fired-but-unretired row would be re-fired.
//
// The post-failure states (ACTIVATION_FAILED, ACTIVATION_CONFLICT) stay out:
// replaying a recorded activation is the admin carrier-retry wrapper's job
// (allowAdminRetry, TN_ADMIN_CARRIER_RETRY_STATUSES) and is not subject to this gate.
// ---------------------------------------------------------------------------

/** Statuses a non-Turning-Point order (INTERNAL / MANUAL / TOLLFREE) may be activated from. */
export const TN_ACTIVATE_PORT_ORDER_ALLOWED_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.FOC_RECEIVED,
  PORT_STATUS.ACTIVATION_PENDING,
];

/** Statuses a Turning Point external order may be activated from. */
export const TN_ACTIVATE_TURNING_POINT_ALLOWED_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.ACTIVATION_PENDING,
];

export const PORT_SUBSTATUS = {
  RESUBMIT_AFTER_REJECTION: 'Resubmit after rejection',
  RESUBMIT_AFTER_SUBMISSION_ISSUE: 'Resubmit after submission issue',
} as const;

/** Days after the anchor day that admins (and the cron) may still activate. 3 → four days total. */
export const TN_ACTIVATION_ADMIN_WINDOW_DAYS = 3;

/**
 * Calendar-day zone for the activation window. Deliberately NOT PORTING_TIME_ZONE
 * (US/Central): due-date validation and slot defaults stay Central; the
 * activate window is an Eastern-day rule (decided 2026-09-17).
 */
export const TN_ACTIVATION_TIME_ZONE = 'US/Eastern';

export interface TnActivationCandidate {
  status?: string | null;
  source?: string | null;
  portType?: string | null;
  focDate?: Date | string | null;
  dueDate?: Date | string | null;
}

export type TnActivationRefusal =
  | 'STATUS_NOT_ACTIVATABLE'
  /** Neither focDate nor dueDate is set — nothing to anchor the window on. */
  | 'NO_ANCHOR_DATE'
  | 'BEFORE_WINDOW'
  | 'AFTER_WINDOW';

export interface TnActivationRuleOptions {
  isAdmin: boolean;
  /** Injected for tests; defaults to the current instant. */
  now?: Date;
}

/** "This order talks to Turning Point" — shared with carrierRetryOrderRefusal. */
function isTurningPointExternalOrder(order: { source?: string | null; portType?: string | null }): boolean {
  return order.source === PORT_SOURCE_TYPES.TURNING_POINT && order.portType !== PORT_TYPES.TOLLFREE;
}

function tnActivateAllowedStatuses(order: { source?: string | null; portType?: string | null }): readonly PortStatus[] {
  return isTurningPointExternalOrder(order)
    ? TN_ACTIVATE_TURNING_POINT_ALLOWED_STATUSES
    : TN_ACTIVATE_PORT_ORDER_ALLOWED_STATUSES;
}

export function isTnActivateStatusAllowed(order: TnActivationCandidate): boolean {
  return !!order.status && (tnActivateAllowedStatuses(order) as readonly string[]).includes(order.status);
}

/** The day the window opens on: granted FOC date, else the due date. */
function tnActivationAnchorDate(order: TnActivationCandidate): Date | null {
  const raw = order.focDate ?? order.dueDate ?? null;
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

/** Whole US/Eastern calendar days from the anchor day to `now`'s day (negative = before). */
export function tnActivationDayOffset(order: TnActivationCandidate, now: Date = new Date()): number | null {
  const anchor = tnActivationAnchorDate(order);
  if (!anchor) return null;
  const anchorDay = moment.tz(anchor, TN_ACTIVATION_TIME_ZONE).startOf('day');
  const today = moment.tz(now, TN_ACTIVATION_TIME_ZONE).startOf('day');
  return today.diff(anchorDay, 'days');
}

/** Null when activation may proceed; otherwise the single reason it may not (status first). */
export function tnActivationRefusal(order: TnActivationCandidate, opts: TnActivationRuleOptions): TnActivationRefusal | null {
  if (!isTnActivateStatusAllowed(order)) return 'STATUS_NOT_ACTIVATABLE';
  const offset = tnActivationDayOffset(order, opts.now);
  if (offset === null) return 'NO_ANCHOR_DATE';
  if (offset < 0) return 'BEFORE_WINDOW';
  const maxOffset = opts.isAdmin ? TN_ACTIVATION_ADMIN_WINDOW_DAYS : 0;
  if (offset > maxOffset) return 'AFTER_WINDOW';
  return null;
}

export function isTnActivationAllowed(order: TnActivationCandidate, opts: TnActivationRuleOptions): boolean {
  return tnActivationRefusal(order, opts) === null;
}

/** Eastern-day label for messages, e.g. "09/22/2026". */
function tnActivationAnchorLabel(order: TnActivationCandidate): string | null {
  const anchor = tnActivationAnchorDate(order);
  return anchor ? moment.tz(anchor, TN_ACTIVATION_TIME_ZONE).format('MM/DD/YYYY') : null;
}

/**
 * One wording for the server error and the UI hint. STATUS keeps the historical
 * "not in an activatable state" substring that existing specs and operators match on.
 */
export function tnActivationRefusalMessage(
  refusal: TnActivationRefusal,
  order: TnActivationCandidate,
  opts: { isAdmin: boolean },
): string {
  const anchor = tnActivationAnchorLabel(order);
  switch (refusal) {
    case 'STATUS_NOT_ACTIVATABLE':
      return isTurningPointExternalOrder(order)
        ? 'not in an activatable state (waiting for Turning Point activate-ready)'
        : 'not in an activatable state';
    case 'NO_ANCHOR_DATE':
      return 'has no FOC date or due date to activate against';
    case 'BEFORE_WINDOW':
      return `can be activated starting on its FOC date (${anchor})`;
    case 'AFTER_WINDOW':
      return opts.isAdmin
        ? `is past its activation window (FOC date ${anchor} plus ${TN_ACTIVATION_ADMIN_WINDOW_DAYS} days)`
        : `can only be activated on its FOC date (${anchor})`;
  }
}

// ---------------------------------------------------------------------------
// Automatic-activation opt-in gate: tnAutoActivationOptInRefusal().
//
// Turning automation ON is allowed on or before the day of the port, for every
// caller — admins included. Same anchor (focDate ?? dueDate) and zone (US/Eastern)
// as tnActivationRefusal, but the window runs the other way: manual activation
// OPENS on the port day (admins may keep activating manually for
// TN_ACTIVATION_ADMIN_WINDOW_DAYS more); opt-in CLOSES at the port day. There is no
// admin grace here, so the rule needs no role and every layer (UI, cpaas-api
// service) applies the same answer — including to portal traffic, which reaches
// cpaas-api as the always-admin L1 account.
//
// Turning automation OFF is never date-gated.
// ---------------------------------------------------------------------------

export type TnAutoActivationOptInCandidate = Pick<TnActivationCandidate, 'focDate' | 'dueDate'>;

export type TnAutoActivationOptInRefusal =
  /** Neither focDate nor dueDate is set — nothing to schedule against. */
  | 'NO_ANCHOR_DATE'
  /** Today is past the port day. */
  | 'AFTER_PORT_DAY';

/** Null when automation may be turned on; otherwise the single reason it may not. */
export function tnAutoActivationOptInRefusal(
  order: TnAutoActivationOptInCandidate,
  opts: { now?: Date } = {},
): TnAutoActivationOptInRefusal | null {
  const offset = tnActivationDayOffset(order, opts.now);
  if (offset === null) return 'NO_ANCHOR_DATE';
  return offset > 0 ? 'AFTER_PORT_DAY' : null;
}

export function isTnAutoActivationOptInAllowed(
  order: TnAutoActivationOptInCandidate,
  opts: { now?: Date } = {},
): boolean {
  return tnAutoActivationOptInRefusal(order, opts) === null;
}

/** One wording for the server error and the UI hint; reads as "Port order X <message>". */
export function tnAutoActivationOptInRefusalMessage(
  refusal: TnAutoActivationOptInRefusal,
  order: TnAutoActivationOptInCandidate,
): string {
  switch (refusal) {
    case 'NO_ANCHOR_DATE':
      return 'has no FOC date or due date to schedule automatic activation against';
    case 'AFTER_PORT_DAY':
      return `is past its port day (${tnActivationAnchorLabel(order)}) — automatic activation can only be turned on on or before the day of the port; activate it manually`;
  }
}

/** The refusal's message (reads as "Port order X <message>"), or null when the turn-on is allowed. */
export function tnAutoActivationOptInError(
  order: TnAutoActivationOptInCandidate,
  opts: { now?: Date } = {},
): string | null {
  const refusal = tnAutoActivationOptInRefusal(order, opts);
  return refusal && tnAutoActivationOptInRefusalMessage(refusal, order);
}

/**
 * The opt-in candidate for an edit that turns automation ON, anchored on focDate ?? the
 * due date the edit leaves in place; null when the edit does not turn it on. Off→on
 * only: re-saving an already opted-in stale order must not start failing on unrelated
 * edits.
 */
export function tnEditAutoActivationOptInCandidate(
  before: TnAutoActivationOptInCandidate & { autoActivation?: boolean | null },
  edit: { autoActivation?: boolean | null; dueDate?: Date | string | null },
): TnAutoActivationOptInCandidate | null {
  if (edit.autoActivation !== true || before.autoActivation) return null;
  return { focDate: before.focDate, dueDate: edit.dueDate ?? before.dueDate };
}

/**
 * The day a turn-on is scheduled on (CpaasPorting.applyAutoActivation): the granted FOC
 * date, else the due date — the same anchor the opt-in rule and the cron window use.
 * An existing row's activateOn day is never kept: a CANCELLED row's is stale, and a
 * PENDING row off the anchor day (an admin's direct schedule, or a row armed before the
 * port day moved) is moved back onto it. The dialogs warn about the latter
 * (tnActivationPendingScheduleMismatch).
 */
export function tnActivationScheduleDay(order: {
  focDate?: Date | string | null;
  dueDate?: Date | string | null;
}): Date | null {
  return tnActivationAnchorDate(order);
}

/**
 * The instant whose Central time-of-day a turn-on keeps when no explicit time is given:
 * the armed PENDING row's activateOn, else the stored due date. A CANCELLED (or fired)
 * row's activateOn is never used. Shared by CpaasPorting.applyAutoActivation and the
 * forms that seed an activation-time control (pickInitialPortingActivationTime).
 */
export function tnActivationTimeSource(order: {
  activationStatus?: string | null;
  activateOn?: Date | string | null;
  dueDate?: Date | string | null;
}): Date | string | null {
  const pendingActivateOn =
    order.activationStatus === PORT_ACTIVATION_STATUSES.PENDING ? order.activateOn : null;
  return pendingActivateOn ?? order.dueDate ?? null;
}

/**
 * The armed PENDING row's activateOn when its (Central) day differs from the schedule
 * day a turn-on would write, else null. Confirming a turn-on moves such a row onto the
 * FOC date, so the Change Activation dialogs show this as a warning first.
 */
export function tnActivationPendingScheduleMismatch(order: {
  activationStatus?: string | null;
  activateOn?: Date | string | null;
  focDate?: Date | string | null;
  dueDate?: Date | string | null;
}): Date | null {
  if (order.activationStatus !== PORT_ACTIVATION_STATUSES.PENDING || !order.activateOn) return null;
  const scheduleDay = tnActivationScheduleDay(order);
  if (!scheduleDay) return null;
  return isSamePortingDay(order.activateOn, scheduleDay) ? null : new Date(order.activateOn);
}

/** Port types the activation scheduler owns a row for (isTnActivationSchedulable). */
export const TN_ACTIVATION_SCHEDULABLE_PORT_TYPES: readonly string[] = [
  PORT_TYPES.INTERNAL,
  PORT_TYPES.EXTERNAL_TO_INTERNAL,
  PORT_TYPES.EXTERNAL_TO_EXTERNAL,
  PORT_TYPES.MANUAL,
  PORT_TYPES.TOLLFREE,
];

export function isTnActivationSchedulable(order: { portType?: string | null }): boolean {
  return !!order.portType && TN_ACTIVATION_SCHEDULABLE_PORT_TYPES.includes(order.portType);
}

/**
 * Activation-row statuses a turn-on may (re)write: no row yet, an armed PENDING row to
 * re-time, or a CANCELLED row left by an earlier opt-out. Fired rows
 * (AWAITING_CONFIRMATION / ACTIVATED / COMPLETED) are never rewritten.
 */
export function isTnActivationRowRearmable(status: string | null | undefined): boolean {
  return (
    !status ||
    status === PORT_ACTIVATION_STATUSES.PENDING ||
    status === PORT_ACTIVATION_STATUSES.CANCELLED
  );
}

export type TnEditActivationRowAction = 'CANCEL' | 'ARM_AT_DUE_DATE' | 'KEEP';

/**
 * What an edit does to the activation row. Shared by the order edit and the split
 * (POR-206), which treats each resulting order as an edit of the original:
 *   flag off                                      → CANCEL (PENDING-only, as cancelTnPortOrderActivation)
 *   flag turned on and row rearmable              → ARM_AT_DUE_DATE (revives a CANCELLED opt-out row)
 *   flag on, due date changed, row absent/PENDING → ARM_AT_DUE_DATE (a cancelled schedule stays cancelled)
 *   otherwise                                     → KEEP
 */
export function tnEditActivationRowAction(
  before: { autoActivation?: boolean | null; activationStatus?: string | null },
  edit: { autoActivation: boolean; dueDateChanged: boolean },
): TnEditActivationRowAction {
  if (!edit.autoActivation) return 'CANCEL';
  if (!before.autoActivation && isTnActivationRowRearmable(before.activationStatus)) return 'ARM_AT_DUE_DATE';
  const retimeable = !before.activationStatus || before.activationStatus === PORT_ACTIVATION_STATUSES.PENDING;
  return edit.dueDateChanged && retimeable ? 'ARM_AT_DUE_DATE' : 'KEEP';
}

/**
 * The day a turn-on actually writes an activation row for, or null when it only sets the
 * flag (non-schedulable port type, or a row that has already fired). The Change
 * Activation dialogs slot-check only these orders — a fired row is never re-timed, so
 * its day must not restrict the chosen time.
 */
export function tnActivationRescheduleDay(order: Parameters<typeof tnActivationScheduleDay>[0] & {
  portType?: string | null;
  activationStatus?: string | null;
}): Date | null {
  if (!isTnActivationSchedulable(order) || !isTnActivationRowRearmable(order.activationStatus)) return null;
  return tnActivationScheduleDay(order);
}

/**
 * Statuses on which Change Activation never touches the flag. One list for the service's
 * setAutoActivation guard, its setAutoActivationForGroup filter and the group dialog's
 * preview, so they cannot drift.
 */
export const TN_AUTO_ACTIVATION_TERMINAL_STATUSES: readonly string[] = [
  PORT_STATUS.COMPLETED,
  PORT_STATUS.PARTIAL_COMPLETE,
  PORT_STATUS.CANCELLED,
  PORT_STATUS.FAILED,
];

/**
 * Would Change Activation attempt this order at all (before the date rule)? One rule for
 * setAutoActivation, setAutoActivationForGroup, the group dialog and both Change
 * Activation buttons. ACTIVATION_IN_PROGRESS is excluded as well as the terminal list:
 * the carrier already has the activation, so there is no schedule left to change. A
 * Remove TNs original is cancel-only (POR-202): rearming it would revive the activation
 * row Remove TNs cancelled, even while that cancel is pending or has failed.
 */
export function isTnAutoActivationChangeable(
  order: { status: string; activationStatus?: string | null; replacedByOrderId?: string | null },
): boolean {
  return !isTnPortOrderReplaced(order)
    && !TN_AUTO_ACTIVATION_TERMINAL_STATUSES.includes(order.status)
    && order.status !== PORT_STATUS.ACTIVATION_IN_PROGRESS
    && order.activationStatus !== PORT_ACTIVATION_STATUSES.COMPLETED;
}

/**
 * Outbound Turning Point calls, keyed by TurningPointApi method name — the value stored in
 * tn_port_order_audit.tp_call. The method fixes the supplement type on the wire
 * (cancel = 1, due-date change = 2, update = 3), so no separate column is needed.
 * Read-only calls (portInDetails, losingSpidsWithAgreements) are deliberately absent:
 * nothing to replay.
 *
 * Lives here rather than in the turning-point lib because the admin retry (cpaas-api)
 * and the row button (frontend) both classify on it, alongside the status vocabularies.
 */
export const TURNING_POINT_CALL = {
  CREATE: 'createPortInOrder',
  CREATE_SOA: 'createSOAOnlyPortInOrder',
  UPDATE: 'updatePortInOrder',
  UPDATE_SOA: 'updateSOAOnlyPortInOrder',
  CHANGE_DUE_DATE: 'changeDueDateForPortInOrder',
  CANCEL: 'cancelPortInOrder',
  CANCEL_SOA: 'cancelSOAOnlyPortInOrder',
  ACTIVATE: 'activatePortInOrder',
} as const;
export type TurningPointCall = (typeof TURNING_POINT_CALL)[keyof typeof TURNING_POINT_CALL];

export function isTnUpdatePortOrderAllowed(status: string | null | undefined): boolean {
  return !!status && (TN_UPDATE_PORT_ORDER_ALLOWED_STATUSES as readonly string[]).includes(status);
}

export function isTnChangeDueDateAllowed(status: string | null | undefined): boolean {
  return !!status && (TN_CHANGE_DUE_DATE_ALLOWED_STATUSES as readonly string[]).includes(status);
}

export function isTnCancelPortOrderAllowed(
  status: string | null | undefined,
  portType: string | null | undefined,
): boolean {
  if (!status) return false;
  const allowed =
    portType === PORT_TYPES.TOLLFREE
      ? TN_CANCEL_TOLLFREE_PORT_ORDER_ALLOWED_STATUSES
      : TN_CANCEL_PORT_ORDER_ALLOWED_STATUSES;
  return (allowed as readonly string[]).includes(status);
}

// POR-202: Remove TNs is offered only before any activate has been attempted and
// while no cancel is in flight. Cancel allow-list minus SUBMISSION_PENDING (TP create
// in flight), the post-activate statuses and CANCELLATION_FAILED, plus HOLD and FAILED
// (no PON; removes in place, and removing every TN cancels locally).
export const TN_REMOVE_PORT_ORDER_TNS_ALLOWED_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.NEW,
  PORT_STATUS.SUBMISSION_FAILED,
  PORT_STATUS.FAILED,
  PORT_STATUS.FOC_PENDING,
  PORT_STATUS.REJECTED,
  PORT_STATUS.UPDATE_POSTED,
  PORT_STATUS.LSR_APPROVED,
  PORT_STATUS.FOC_RECEIVED,
  PORT_STATUS.DATE_CHANGE_POSTED,
  PORT_STATUS.DATE_CHANGE_PENDING,
  PORT_STATUS.ACTIVATION_PENDING,
  PORT_STATUS.HOLD,
];

export interface TnRemovePortOrderTnsEligibilityInput {
  status: string;
  portType: string;
  submissionMode?: string | null;
  activationStatus?: string | null;
  replacedByOrderId?: string | null;
}

/**
 * The one Remove TNs eligibility rule, shared by the order card, portal-api and
 * cpaas-api. Toll-free and manually submitted Turning Point orders are out of scope,
 * a replaced original is cancel-only, and the activation row must never have fired.
 */
export function isTnRemovePortOrderTnsAllowed(order: TnRemovePortOrderTnsEligibilityInput): boolean {
  if (order.portType === PORT_TYPES.TOLLFREE) return false;
  const external =
    order.portType === PORT_TYPES.EXTERNAL_TO_INTERNAL || order.portType === PORT_TYPES.EXTERNAL_TO_EXTERNAL;
  if (external && order.submissionMode === PORT_SUBMISSION_MODES.MANUAL) return false;
  if (isTnPortOrderReplaced(order)) return false;
  if (!isTnActivationRowRearmable(order.activationStatus)) return false;
  return (TN_REMOVE_PORT_ORDER_TNS_ALLOWED_STATUSES as readonly string[]).includes(order.status);
}

// POR-206: Split order is offered exactly where Remove TNs is (decision 7): the same
// statuses, the same exclusions, the same replaced-original lock.
export function isTnSplitPortOrderAllowed(order: TnRemovePortOrderTnsEligibilityInput): boolean {
  return isTnRemovePortOrderTnsAllowed(order);
}

// POR-206: the most orders one split file may produce. Each group clones documents and
// addresses inside one transaction, and each becomes a sequential TP create at release.
export const TN_SPLIT_PORT_ORDER_MAX_GROUPS = 50;

/**
 * True when the order is actually submitted to Turning Point. INTERNAL, MANUAL and
 * PORT_IN_MAYBE orders never reach TP, nor do orders from other sources. TOLLFREE keeps
 * its own (Somos) paths. portType and orderType are independent axes; this mirrors the
 * create-path isTpEligible gate.
 */
export function isTurningPointPortOrder(order: {
  portType?: string | null;
  orderType?: string | null;
  source?: string | null;
}): boolean {
  return order.portType !== PORT_TYPES.INTERNAL
    && order.portType !== PORT_TYPES.MANUAL
    && order.portType !== PORT_TYPES.TOLLFREE
    && order.orderType !== ORDER_TYPE.PORT_IN_MAYBE
    && order.source === PORT_SOURCE_TYPES.TURNING_POINT;
}

/**
 * True when Remove TNs must keep the order's TNs on a new HOLD order and cancel the
 * original: a Turning Point order that already has a PON. Shrinking it in place would
 * desync us from the carrier, which still holds every number under that PON. POR-202:
 * shared so the order card's Remove TNs dialog explains the same branch the backend takes.
 */
export function isRemoveTnsHoldClone(order: {
  portType: string;
  orderType?: string | null;
  source?: string | null;
  sourceId?: string | null;
}): boolean {
  return isTurningPointPortOrder(order) && !!order.sourceId;
}

// POR-202: replaced originals are locked to cancel-only while Turning Point confirms the cancel.
export function isTnPortOrderReplaced(order: { replacedByOrderId?: string | null }): boolean {
  return !!order.replacedByOrderId;
}

// ---------------------------------------------------------------------------
// Admin carrier retry (POR-168): replay the last recorded Turning Point call.
// ---------------------------------------------------------------------------

/**
 * Statuses the admin carrier-retry wrapper (tnRetryPortOrderCarrierAction) may act
 * from. PARTIAL_COMPLETE is excluded on purpose (decision 5). Eligibility only — the
 * call to replay comes from the audit record, not from the status.
 */
export const TN_ADMIN_CARRIER_RETRY_STATUSES: readonly PortStatus[] = [
  PORT_STATUS.SUBMISSION_FAILED,
  PORT_STATUS.FAILED,
  PORT_STATUS.ACTIVATION_CONFLICT,
  PORT_STATUS.ACTIVATION_FAILED,
  PORT_STATUS.CANCELLATION_FAILED,
];

export function isTnAdminCarrierRetryAllowed(status: string | null | undefined): boolean {
  return !!status && (TN_ADMIN_CARRIER_RETRY_STATUSES as readonly string[]).includes(status);
}

/**
 * What a replay of a recorded call does. Shared by the wrapper (which performs it), the
 * order's `lastCarrierCall` field (which exposes it to admins) and the row button (which
 * labels it), so the label and the server's action cannot disagree.
 */
export type TnCarrierReplayKind = 'RESUBMIT' | 'RETRY_UPDATE' | 'CHANGE_DUE_DATE' | 'ACTIVATE' | 'CANCEL';

/** The audit fields the classifier looks at (a subset of the latest-call reader's row). */
export interface TnCarrierReplaySource {
  tpCall: string | null;
  action: string | null;
  tpError: string | null;
  metadata: Record<string, unknown> | null;
}

/**
 * Maps the latest recorded outbound call to the replay that re-sends it. Null when the
 * recorded call cannot be replayed (unknown method, or a failed update with no stored
 * input).
 */
export function classifyCarrierReplay(row: TnCarrierReplaySource): TnCarrierReplayKind | null {
  switch (row.tpCall) {
    case TURNING_POINT_CALL.CREATE:
    case TURNING_POINT_CALL.CREATE_SOA:
      return 'RESUBMIT';
    case TURNING_POINT_CALL.UPDATE_SOA:
      // SOA-only due-date changes ride the modifysv endpoint (changeDueDate's SOA branch),
      // so the audit action, not the method, tells them apart from a field update.
      if (row.action === 'CHANGE_DUE_DATE') return 'CHANGE_DUE_DATE';
      return classifyUpdateReplay(row);
    case TURNING_POINT_CALL.UPDATE:
      return classifyUpdateReplay(row);
    case TURNING_POINT_CALL.CHANGE_DUE_DATE:
      return 'CHANGE_DUE_DATE';
    case TURNING_POINT_CALL.ACTIVATE:
      return 'ACTIVATE';
    case TURNING_POINT_CALL.CANCEL:
    case TURNING_POINT_CALL.CANCEL_SOA:
      return 'CANCEL';
    default:
      return null;
  }
}

// A field update persists to the row only after Turning Point accepts it, so a FAILED
// update left the row stale — the only faithful replay is the input it recorded. A
// succeeded update is reflected by the row and re-sends as-is. An as-is resubmit
// (MANUAL_SUBMISSION action: the type-3 supplement of an order that already holds a
// PON) sent the row's current snapshot, so nothing is stale whether or not it failed —
// a transient carrier failure there must stay retryable.
function classifyUpdateReplay(row: TnCarrierReplaySource): TnCarrierReplayKind | null {
  if (row.action === 'MANUAL_SUBMISSION') return 'RESUBMIT';
  if (!row.tpError) return 'RESUBMIT';
  return row.metadata?.['updateInput'] ? 'RETRY_UPDATE' : null;
}

/**
 * Whether a classified replay may run from the order's current status. RETRY_UPDATE goes
 * through the normal update guard — a stale edit is not re-applied to an order that has
 * moved on. Shared by the wrapper (server-side refusal) and the card button (label).
 */
export function isCarrierReplayAllowed(kind: TnCarrierReplayKind, status: string | null | undefined): boolean {
  if (!isTnAdminCarrierRetryAllowed(status)) return false;
  return kind === 'RETRY_UPDATE' ? isTnUpdatePortOrderAllowed(status) : true;
}

const TN_CARRIER_REPLAY_KINDS: readonly TnCarrierReplayKind[] = [
  'RESUBMIT', 'RETRY_UPDATE', 'CHANGE_DUE_DATE', 'ACTIVATE', 'CANCEL',
];

/** The order fields the retry decision looks at. */
export interface TnCarrierReplayCandidate {
  status?: string | null;
  source?: string | null;
  portType?: string | null;
  sourceId?: string | null;
}

export type TnCarrierReplayRefusal =
  /** Not a Turning Point order, or toll-free — those keep their own recovery paths. */
  | 'NOT_ELIGIBLE_ORDER'
  | 'STATUS_NOT_RETRYABLE'
  /** No recorded (replayable) call, and the order is not a failed create without a PON. */
  | 'NOTHING_TO_REPLAY'
  /** A recorded call whose replay is not allowed from the current status (RETRY_UPDATE). */
  | 'KIND_NOT_ALLOWED_FROM_STATUS';

// A plain shape rather than a discriminated union: the workspace compiles without
// strictNullChecks, so a `kind: null` member would not narrow.
export interface TnCarrierReplayDecision {
  kind: TnCarrierReplayKind | null;
  /** Where the replay comes from; set when kind is non-null. */
  replaySource: 'AUDIT' | 'FALLBACK_FAILED_CREATE' | null;
  /** Why there is no replay; set when kind is null. */
  refusal: TnCarrierReplayRefusal | null;
}

const refused = (refusal: TnCarrierReplayRefusal): TnCarrierReplayDecision =>
  ({ kind: null, replaySource: null, refusal });

/**
 * The order-level half of resolveCarrierReplay — everything that can be decided before the
 * audit record is read. The wrapper applies it first so an ineligible order costs no audit
 * query; null means the order itself is eligible.
 */
export function carrierRetryOrderRefusal(order: TnCarrierReplayCandidate): TnCarrierReplayRefusal | null {
  if (!isTurningPointExternalOrder(order)) {
    return 'NOT_ELIGIBLE_ORDER';
  }
  if (!isTnAdminCarrierRetryAllowed(order.status)) {
    return 'STATUS_NOT_RETRYABLE';
  }
  return null;
}

/**
 * The one admin carrier-retry decision, shared by the wrapper (tnRetryPortOrderCarrierAction,
 * which turns a refusal into its error) and the row button (which shows nothing on a
 * refusal), so the two cannot drift. `recordedKind` is the classified latest recorded call
 * (the order's `lastCarrierCall`); null/unknown means there is nothing recorded to replay.
 *
 * FAILED with no PON is the only no-record fallback: FAILED is written only when Nuso's own
 * create threw before a PON existed, so a fresh create is the replay.
 */
export function resolveCarrierReplay(
  order: TnCarrierReplayCandidate,
  recordedKind: string | null | undefined,
): TnCarrierReplayDecision {
  const orderRefusal = carrierRetryOrderRefusal(order);
  if (orderRefusal) return refused(orderRefusal);
  const kind = (TN_CARRIER_REPLAY_KINDS as readonly string[]).includes(recordedKind ?? '')
    ? (recordedKind as TnCarrierReplayKind)
    : null;
  if (kind) {
    return isCarrierReplayAllowed(kind, order.status)
      ? { kind, replaySource: 'AUDIT', refusal: null }
      : refused('KIND_NOT_ALLOWED_FROM_STATUS');
  }
  if (order.status === PORT_STATUS.FAILED && !order.sourceId) {
    return { kind: 'RESUBMIT', replaySource: 'FALLBACK_FAILED_CREATE', refusal: null };
  }
  return refused('NOTHING_TO_REPLAY');
}

const PORT_EVENT_STATUS_MAP: Record<
  PortEvent,
  PortStatus
> = {
  [PORT_EVENT.SUBMIT_NEW_PORT_ORDER]: PORT_STATUS.SUBMISSION_PENDING,
  [PORT_EVENT.UPDATE_ORDER_DETAILS]: PORT_STATUS.UPDATE_POSTED,
  [PORT_EVENT.CHANGE_PORT_DATE]: PORT_STATUS.DATE_CHANGE_POSTED,
  [PORT_EVENT.CANCEL_PORT_ORDER]: PORT_STATUS.CANCELLATION_PENDING,
  [PORT_EVENT.RESUBMIT_AFTER_REJECTION]: PORT_STATUS.FOC_PENDING,
  [PORT_EVENT.ACTIVATE_PORT]: PORT_STATUS.ACTIVATION_IN_PROGRESS,
  [PORT_EVENT.CHANGE_PORT_TIME]: PORT_STATUS.NA,
  [PORT_EVENT.SERVICE_REQUEST_RECEIVED]: PORT_STATUS.NA,
  [PORT_EVENT.CSR_REQUESTED]: PORT_STATUS.NA,
  [PORT_EVENT.CSR_IN_PROGRESS]: PORT_STATUS.NA,
  [PORT_EVENT.CSR_RECEIVED]: PORT_STATUS.NA,
  [PORT_EVENT.CSR_FAILED]: PORT_STATUS.NA,
  [PORT_EVENT.LSR_IN_PROGRESS]: PORT_STATUS.FOC_PENDING,
  [PORT_EVENT.LSR_SUPPLEMENT_IN_PROGRESS]: PORT_STATUS.NA,
  [PORT_EVENT.SUBMITTED]: PORT_STATUS.FOC_PENDING,
  [PORT_EVENT.PENDING_FROM_CARRIER]: PORT_STATUS.FOC_PENDING,
  [PORT_EVENT.FIRM_ORDER_CONFIRMATION]: PORT_STATUS.LSR_APPROVED,
  [PORT_EVENT.SUPPLEMENT_RECEIVED]: PORT_STATUS.UPDATE_POSTED,
  [PORT_EVENT.SUBMIT_FAILED]: PORT_STATUS.NA,
  [PORT_EVENT.VALIDATION_FAILED]: PORT_STATUS.SUBMISSION_FAILED,
  [PORT_EVENT.SERVICE_REQUEST_INITIATION_FAILED]: PORT_STATUS.SUBMISSION_FAILED,
  [PORT_EVENT.REJECTED]: PORT_STATUS.REJECTED,
  [PORT_EVENT.JEOPARDY]: PORT_STATUS.NA,
  [PORT_EVENT.PROVIDER_NOTIFICATION]: PORT_STATUS.NA,
  [PORT_EVENT.PROVIDER_INITIATED_CANCELLATION]: PORT_STATUS.NA,
  [PORT_EVENT.CANCEL_CONFIRMATION]: PORT_STATUS.CANCELLED,
  [PORT_EVENT.CANCELLED]: PORT_STATUS.CANCELLED,
  [PORT_EVENT.SERVICE_ORDER_CONFIRMATION]: PORT_STATUS.NA,
  [PORT_EVENT.BILLING_COMPLETION_NOTICE]: PORT_STATUS.NA,
  [PORT_EVENT.COMPLETED]: PORT_STATUS.COMPLETED,
  [PORT_EVENT.PARTIAL_COMPLETED]: PORT_STATUS.PARTIAL_COMPLETE,
  // not using TP scheduled activation — these events should not occur
  [PORT_EVENT.PORT_SCHEDULED]: PORT_STATUS.NA,
  [PORT_EVENT.PORT_RESCHEDULED]: PORT_STATUS.NA,
  [PORT_EVENT.PORT_RESCHEDULED_FAILED]: PORT_STATUS.NA,
  [PORT_EVENT.PORT_SCHEDULED_FAILED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_ORDER_RECEIVED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_CREATE_INITIATED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_CREATE_SUBMITTED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_CREATE_REJECTED]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_CREATE_FAILED]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_PENDING]: PORT_STATUS.FOC_RECEIVED,
  [PORT_EVENT.SV_NEW]: PORT_STATUS.NA,
  [PORT_EVENT.SV_MODIFY_INITIATED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_MODIFY_SUBMITTED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_MODIFY_REJECTED]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_MODIFY_FAILED]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_CONCURRED]: PORT_STATUS.FOC_RECEIVED,
  [PORT_EVENT.SV_CONCURRENCE_SENT]: PORT_STATUS.FOC_RECEIVED,
  [PORT_EVENT.SV_ACTIVATE_READY]: PORT_STATUS.ACTIVATION_PENDING,
  [PORT_EVENT.SV_PARTIAL_FAILED]: PORT_STATUS.ACTIVATION_FAILED,
  [PORT_EVENT.SV_CONFLICT]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_ERROR]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_VALIDATION_FAILED]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_OLD]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_CONFLICT_RECEIVED]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_REL_FAILURE]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_REMOVE_FROM_CONFLICT_INITIATED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_REMOVE_FROM_CONFLICT_SUBMITTED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_REMOVE_FROM_CONFLICT_REJECTED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_REMOVE_FROM_CONFLICT_FAILED]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_REMOVE_CONFLICT_RECEIVED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_ACTIVATE_RECEIVED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_ACTIVATE_INITIATED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_ACTIVATE_SUBMITTED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_ACTIVATE_REJECTED]: PORT_STATUS.ACTIVATION_CONFLICT,
  [PORT_EVENT.SV_ACTIVATE_FAILED]: PORT_STATUS.ACTIVATION_FAILED,
  [PORT_EVENT.SV_ACTIVATED]: PORT_STATUS.COMPLETED,
  [PORT_EVENT.SOA_COMPLETED]: PORT_STATUS.COMPLETED,
  [PORT_EVENT.SV_CANCEL_INITIATED]: PORT_STATUS.CANCELLATION_PENDING,
  [PORT_EVENT.SV_CANCEL_SUBMITTED]: PORT_STATUS.CANCELLATION_PENDING,
  [PORT_EVENT.SV_CANCEL_PENDING]: PORT_STATUS.CANCELLATION_PENDING,
  [PORT_EVENT.SV_CANCEL_REJECTED]: PORT_STATUS.CANCELLATION_FAILED,
  [PORT_EVENT.SV_CANCEL_FAILED]: PORT_STATUS.CANCELLATION_FAILED,
  [PORT_EVENT.SV_CANCELLED]: PORT_STATUS.CANCELLED,
  [PORT_EVENT.SOA_CANCELLED]: PORT_STATUS.CANCELLED,
  [PORT_EVENT.SV_MODIFY_CANCEL_UNDO_INITIATED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_MODIFY_CANCEL_UNDO_SUBMITTED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_MODIFY_CANCEL_UNDO_REJECTED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_MODIFY_CANCEL_UNDO_FAILED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_DISCONNECT_INITIATED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_DISCONNECT_SUBMITTED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_DISCONNECT_REJECTED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_DISCONNECT_FAILED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_DISC_PENDING]: PORT_STATUS.NA,
  [PORT_EVENT.SV_DISC_SUCCESS]: PORT_STATUS.NA,
  [PORT_EVENT.SV_FAILED]: PORT_STATUS.ACTIVATION_FAILED,
  [PORT_EVENT.SV_MODIFY_REQUEST_RECEIVED]: PORT_STATUS.NA,
  [PORT_EVENT.SV_CANCEL_REQUEST_RECEIVED]: PORT_STATUS.CANCELLATION_PENDING,
  [PORT_EVENT.RESP_ORG_CHANGE_SUBMITTED]: PORT_STATUS.FOC_PENDING,
  [PORT_EVENT.RESP_ORG_UPDATED]: PORT_STATUS.LSR_APPROVED,
  [PORT_EVENT.NUMBER_HAS_BEEN_LOADED_TO_CIC]: PORT_STATUS.FOC_RECEIVED,
  [PORT_EVENT.TEMPLATE_UPDATED]: PORT_STATUS.COMPLETED,
  // An internal port waits in FOC_PENDING until its Salesforce Port Out case is decided.
  [PORT_EVENT.INTERNAL_PORT_CREATED]: PORT_STATUS.FOC_PENDING,
  [PORT_EVENT.INTERNAL_PORT_OUT_APPROVED]: PORT_STATUS.FOC_RECEIVED,
  [PORT_EVENT.INTERNAL_PORT_OUT_REJECTED]: PORT_STATUS.REJECTED,
  [PORT_EVENT.INTERNAL_PORT_ACTIVATED]: PORT_STATUS.COMPLETED,
};

// A cancel supplement (supplementType 1) reuses these statuses on its way to `Cancelled`;
// the order stays CANCELLATION_PENDING until `Cancelled` itself, which is also what
// releases its HOLD orders.
const CANCEL_SUPPLEMENT_PENDING_EVENTS: ReadonlySet<string> = new Set<PortEvent>([
  PORT_EVENT.SUPPLEMENT_RECEIVED,
  PORT_EVENT.LSR_SUPPLEMENT_IN_PROGRESS,
  PORT_EVENT.SUBMITTED,
  PORT_EVENT.CANCEL_CONFIRMATION,
]);

export function mapEventStatus(event: PortEvent | string | undefined, supplementType?: number): PortStatus {
  if (event !== undefined && Object.prototype.hasOwnProperty.call(PORT_EVENT_STATUS_MAP, event)) {
    if (event === PORT_EVENT.SUPPLEMENT_RECEIVED && supplementType === 2) {
      return PORT_STATUS.DATE_CHANGE_PENDING;
    }

    if (supplementType === 1 && CANCEL_SUPPLEMENT_PENDING_EVENTS.has(event)) {
      return PORT_STATUS.CANCELLATION_PENDING;
    }

    return PORT_EVENT_STATUS_MAP[event as PortEvent];
  }
  else {
    return PORT_STATUS.UNKNOWN;
  }
}

export interface TnCreatePortOrderInput {
  accountId: number;
  customerId?: number;
  portalType: string;
  // When set, attach the new sub-order(s) to this existing port order group
  // instead of starting a new group.
  portOrderGroupId?: string;
  tns: string[];
  // MANUAL create flow bypasses portability and uses this gaining SPID.
  portType?: PortType | string;
  gainingSpid?: string;
  portabilityCheckId?: string;
  sourceCustomId?: string;
  salesforceCaseNumber?: string | null;
  salesforceCaseId?: string | null;
  isEarliestPossible: boolean;
  dueDate?: Date;
  loaDate?: Date;
  // Opt in (default) to scheduled auto-activation. When false, no activation
  // row is created and the due time-of-day is optional (server defaults it to
  // 7:00 AM Central).
  autoActivation?: boolean;
  isFullPort: boolean;
  isResidential: boolean;
  businessName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  authorizationName: string;
  billingTelephoneNumber: string;
  newBillingTelephoneNumber?: string;
  accountNumber: string;
  accountPin?: string;
  tnServiceAddresses: TnCreatePortOrderTnServiceAddressInput[];
  remarks?: string;
  notes?: string;
  notificationEmails?: string[];
  tfLOAContentDocumentIds?: string[];
  tfLOAFileIds?: string[];
  // Admin-only override set by the UI after confirming the off-net porting warning.
  // Silently ignored for non-admin callers server-side.
  allowOffnetPortingWhenDisabled?: boolean;
}

export interface TfPortOrderROCResubmissionInput {
  portOrderId: string;
  tfLOAContentDocumentIds?: string[];
  tfLOAFileIds?: string[];
}

export interface TnUpdatePortOrderInput {
  accountId: number;
  customerId?: number;
  portalType: string;
  orderId: string;
  tns: string[];
  salesforceCaseNumber?: string | null;
  salesforceCaseId?: string | null;
  isEarliestPossible: boolean;
  dueDate?: Date;
  loaDate?: Date;
  // Opt in/out of scheduled auto-activation. Omitted keeps the order's current
  // value. When false, the queue row is cancelled and the due time-of-day is
  // optional (server defaults it to 7:00 AM Central).
  autoActivation?: boolean;
  isFullPort: boolean;
  isResidential: boolean;
  businessName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  authorizationName: string;
  billingTelephoneNumber: string;
  newBillingTelephoneNumber?: string;
  accountNumber: string;
  accountPin?: string;
  tnServiceAddresses: TnCreatePortOrderTnServiceAddressInput[];
  remarks?: string;
  notes?: string;
  notificationEmails?: string[];
}

export interface TnUpdatePortOrderWithPreProvisionInput extends TnUpdatePortOrderInput {
  preProvisionSettings?: Record<string, unknown> | null;
  preProvisionTnOverrides?: Array<{ tnE164: string; settings: Record<string, unknown> }>;
}

export interface TnCreatePortOrderTnServiceAddressInput {
  tns: string[];
  address: TnCreatePortOrderAddressInput;
}

export interface TnCreatePortOrderAddressInput {
  numberPrefix?: string;
  number: string;
  numberSuffix?: string;
  streetPreDirectional?: string;
  streetName: string;
  streetSuffix?: string;
  streetPostDirectional?: string;
  locationDesignator1?: string;
  locationValue1?: string;
  locationDesignator2?: string;
  locationValue2?: string;
  locationDesignator3?: string;
  locationValue3?: string;
  city: string;
  stateProvince: string;
  zipCode: string;
  country: string;
}

export interface TnPortOrderError {
  errorCode: string;
  errorMessage: string;
  // The carrier's free-text elaboration on errorMessage. Already persisted in
  // tn_port_order.errors verbatim; for partial rejections it carries the actual
  // reason (e.g. "Invalid Account Number").
  additionalErrorMessage?: string;
}

export interface TnPortOrderTnServiceAddress {
  tns: string[];
  tnE164s: string[];
  address: TnPortOrderAddress;
}

export interface TnPortOrderAddress {
  id: string;
  numberPrefix?: string;
  number: string;
  numberSuffix?: string;
  streetPreDirectional?: string;
  streetName: string;
  streetSuffix?: string;
  streetPostDirectional?: string;
  locationDesignator1?: string;
  locationValue1?: string;
  locationDesignator2?: string;
  locationValue2?: string;
  locationDesignator3?: string;
  locationValue3?: string;
  city: string;
  stateProvince: string;
  zipCode: string;
  country: string;
}

export interface TnUpdateServiceAddressInput {
  id: string;
  number?: string;
  streetName?: string;
  city?: string;
  stateProvince?: string;
  zipCode?: string;
  country?: string;
}

export interface TnPortOrderTnLrn {
  spid: string;
  lrn: string;
  tn: string;
  tnE164: string;
}
export interface TnPortOrderDetails {
  orderId: string;
  displayId?: string;
  legacyOrderId?: number;
  portOrderId: string;
  portOrderGroupId?: string;
  pon: string;
  orderType: string;
  accountId: number;
  accountName: string;
  customerId?: number;
  userId: number;
  userEmail: string;
  tnE164s: string[];
  tns: string[];
  source: string;
  portType: string;
  losingSpid: string;
  externalGainingSpid: string;
  sourceId: string;
  submissionMode: string;
  sourceCustomId: string;
  salesforceCaseNumber?: string | null;
  salesforceCaseId?: string | null;
  soaOnly: boolean;
  isEarliestPossible: boolean;
  // POR-229 admin billing flags; set only through tnSetPortOrderBillingFlags.
  isExpedited: boolean;
  cancelNotBillable: boolean;
  // INTERNAL only: the Salesforce Port Out case whose Approve/Reject moves this order.
  internalPortOutCaseId?: string | null;
  dueDate: Date;
  loaDate: Date;
  focDate: Date;
  autoActivation: boolean;
  // How many automated corrective supplements have been sent for a Turning Point
  // partial rejection on this order. Capped; see claimPartialRejectionAttempt.
  partialRejectAttempts?: number;
  isFullPort: boolean;
  isResidential: boolean;
  businessName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  authorizationName: string;
  billingTelephoneNumber: string;
  newBillingTelephoneNumber: string;
  accountNumber: string;
  accountPin: string;
  status: string;
  substatus?: string | null;
  // Null until a vendor event has been recorded (NEW / TOLLFREE_NEW birth states).
  orderStatus: string | null;
  errors: TnPortOrderError[];
  notificationEmails: string[];
  remarks: string;
  assignedTo: number;
  createdAt: Date;
  updatedAt: Date;
  tnServiceAddresses: TnPortOrderTnServiceAddress[];
  tnLrns: TnPortOrderTnLrn[];
  activateOn: Date;
  activatedAt?: Date;
  activationStatus?: string;
  // How many notes this order has, shipped with the group details response so
  // the detail page's poll does not fan out a tnPortOrderNotes query per order.
  // Respects the caller's internal-note visibility. The notes themselves are
  // fetched by `tnPortOrderNotes(portOrderId)` when an order's notes dialog opens —
  // there is deliberately no notes array hanging off the order.
  noteCount?: number;
  // How many of those the caller's side has not read yet (porting dashboard "New
  // Messages"). Read state is per side, not per person, so this is the same number
  // for every Nuso admin, and for every user on the customer side.
  unreadNoteCount?: number;
  // Replay kind of the latest recorded Turning Point call for this order (POR-168), so
  // the admin row button is labelled for what tnRetryPortOrderCarrierAction will do.
  // Populated for admin callers only; null for everyone else and for orders with no
  // recorded call.
  lastCarrierCall?: TnCarrierReplayKind | null;
  roc?: TfRocDetails;
  // POR-202 replacement link, as tn_order ids. replacesOrderId is set on a HOLD order
  // created by Remove TNs or a split; replacedByOrderIds on the original it replaces,
  // oldest first (POR-206: a split gives it several), and replacedByOrderId is the first.
  replacesOrderId?: string | null;
  replacedByOrderId?: string | null;
  replacedByOrderIds?: string[] | null;
}

export interface TfRocDetails {
  id: string;
  status: string;
  submittedAt?: Date;
  rocId: string;
  portOrderId: string;
  portOrderTargetCarrier?: string;
  tns: TfRocTn[];
}

export interface TfRocTn {
  id: string;
  did: string;
  status: string;
  activationStatus: string;
  rocProcessed: boolean;
  portOrderId: string;
}

export type TnPortOrder = Omit<TnPortOrderDetails, 'tnServiceAddresses' | 'tnLrns'>;

export interface TnPortOrderNote {
  id: string;
  portOrderId: string;
  type: 'internal' | 'external';
  body: string;
  // Which directory portalUserId belongs to. The portal and the sidecar have
  // separate user tables whose ids overlap, so the id alone does not identify
  // anyone; null when the writer was automation.
  actorSource: ActorSource;
  portalUserId: number | null;
  author: string;
  // Which side wrote it. null for SYSTEM notes, which belong to neither side, and
  // for notes written before read tracking existed.
  authorSide: ActorSide | null;
  // When the other side read it, and who. Read state is per side: one reader marks
  // it read for everyone on that side.
  readAt: Date | null;
  readByActorSource: ActorSource | null;
  readByUserId: number | null;
  readByName: string | null;
  // Whether it is new to the caller's side; computed by tnPortOrderNotes with
  // isTnPortOrderNoteUnreadFor, absent on rows read straight from the table.
  unread?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Whether a note is new to the viewer's side: an external note the other side wrote
 * that nobody on the viewer's side has read. Internal and SYSTEM notes never are.
 * Mirrors the SQL in cpaas-porting-sql's unreadNotesFrom, which the counts use.
 */
export function isTnPortOrderNoteUnreadFor(
  note: Pick<TnPortOrderNote, 'type' | 'authorSide' | 'readAt'>,
  viewerSide: ActorSide | null,
): boolean {
  return !!viewerSide
    && note.type === 'external'
    && !!note.authorSide
    && note.authorSide !== viewerSide
    && !note.readAt;
}

export const TN_PORT_ORDER_DOCUMENT_TYPES = {
  LOA: 'LOA',
  INVOICE: 'INVOICE',
  CSR: 'CSR',
  OTHER: 'OTHER',
} as const;

export type TnPortOrderDocumentType = keyof typeof TN_PORT_ORDER_DOCUMENT_TYPES;

export interface TnPortOrderDocument {
  id: string;
  legacyAttachmentId?: number;
  orderId: string;
  documentType: TnPortOrderDocumentType;
  documentName: string;
  filename: string;
  originalFilename: string;
  // See TnPortOrderNote.actorSource. Generated documents carry SYSTEM with no user id,
  // which is what the hardcoded portalUserId of 1 used to stand in for.
  actorSource: ActorSource;
  portalUserId: number | null;
  uploadedBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface TnPortOrderPreProvisionSettings {
  id: string;
  portOrderId: string;
  tnE164: string;
  settings: Record<string, unknown>;
  status: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface TnPortOrderPreProvisionSettingsChange {
  before: TnPortOrderPreProvisionSettings | null;
  after: TnPortOrderPreProvisionSettings;
}

export const PORT_ACTIVATION_STATUSES = {
  ACTIVATED: 'ACTIVATED',
  AWAITING_CONFIRMATION: 'AWAITING_CONFIRMATION',
  PENDING: 'PENDING',
  FAILED: 'FAILED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const;

/**
 * Activation-row statuses a firing may overwrite.
 *
 * PENDING is armed-and-unfired, FAILED is a previous attempt worth superseding, and
 * CANCELLED is an opted-out order someone is activating by hand. AWAITING_CONFIRMATION
 * is excluded on purpose: it means a firing is already in flight, so failing to claim
 * it is how a second caller learns not to send its own carrier call. ACTIVATED and
 * COMPLETED are terminal — a toll-free order can reach COMPLETED from its own monitor
 * moments after firing, and regressing that row would strand it, since nothing polls
 * back over it.
 */
export const TN_ACTIVATION_CLAIMABLE_STATUSES: readonly string[] = [
  PORT_ACTIVATION_STATUSES.PENDING,
  PORT_ACTIVATION_STATUSES.FAILED,
  PORT_ACTIVATION_STATUSES.CANCELLED,
];

/**
 * What the activation row's status becomes once an activation has actually been SENT.
 *
 * TOLLFREE and MANUAL complete locally, so firing is the completion. Everything else
 * goes to the carrier and waits on a webhook, so the row records that we are waiting.
 *
 * Shared by the scheduled cron and the manual mutation because the two must not
 * disagree about what a fired row looks like: the cron's own SELECT reads these rows to
 * decide what still needs activating, so a path that fires without retiring its row is
 * indistinguishable from one that never fired.
 */
export function activationStatusAfterFiring(portType: string | null | undefined): string {
  return portType === PORT_TYPES.TOLLFREE || portType === PORT_TYPES.MANUAL
    ? PORT_ACTIVATION_STATUSES.ACTIVATED
    : PORT_ACTIVATION_STATUSES.AWAITING_CONFIRMATION;
}

export const FAILED_ROC_NUMBER_STATUSES = [ 'DECLINED', 'EXPIRED', 'INVALID', 'PORT_FAILED' ];
export const TERMINAL_ROC_NUMBER_STATUSES = [ 'PORTED', ...FAILED_ROC_NUMBER_STATUSES ];

export function mapLataCarrier(carrier: string): string {
  switch (carrier) {
    case '5102': return 'WCA-5102';
    case '0222': return 'MCI-0222';
    case '0288': return 'ATX-0288';
    default: return carrier;
  }
}

export const CACHE_KEY_TF_ROC_PROCESSING = 'cpaas:tf:roc:processing';
export const CACHE_KEY_TF_ACTIVATION = 'cpaas:tf:activation';
